<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" width="480" max-width="620" @click:outside="close">
    <v-card class="surface lighten-1">
      <v-card-title v-once primary-title>
        {{ $t('dialogs.assignmentsExpirationTime.title') }}
      </v-card-title>

      <!-- eslint-disable-next-line vue/no-v-html -->
      <v-card-subtitle class="pt-3 pb-0" v-html="description" />

      <v-card-text class="py-2 px-6">
        <v-form>
          <v-row class="pt-2">
            <v-col>
              <EcsDateTimePicker
                :value="expirationTime"
                :label="$t('labels.expirationTime')"
                :time-format="applicationSettings.timeFormat"
                :time-zone="applicationSettings.timeZoneUtcOffset"
                @change="onDateTimeChange"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <div class="agents-container surface">
        <div v-for="(group, index) in availableAgentsGrouped" :key="index">
          <div class="agents-group-title">
            <div class="agents-group-label">{{ group.label }}</div>
            <div class="agents-group-selection">
              <span :class="{ 'primary--text': group.selectedAgents.length === group.agents.length }">{{
                $t('labels.selectAll')
              }}</span>
              <v-simple-checkbox
                :ripple="false"
                :value="group.selectedAgents.length === group.agents.length"
                :indeterminate="!!group.selectedAgents.length && group.selectedAgents.length !== group.agents.length"
                class="agents-group-selection-box ml-2"
                :color="group.selectedAgents.length === group.agents.length ? 'var(--v-primary-base)' : 'inherit'"
                @click="group.onSelectAll"
              />
            </div>
          </div>
          <div class="agents-group-container">
            <div v-for="agent in group.agents" :key="agent.id" class="agent">
              <div class="agent-avatar">
                <EcsAvatar :contact-id="agent.id" :size="28" />
              </div>
              <div class="agent-content">
                <div class="name">{{ agent.name }}</div>
                <div class="details">
                  {{ $t('expirationTime', { time: getExpirationTimeFormatted(agent.expirationTime) }) }}
                </div>
              </div>
              <v-checkbox
                v-model="agent.selected"
                dense
                hide-details
                :ripple="false"
                class="agent-selection"
              ></v-checkbox>
            </div>
          </div>
        </div>
      </div>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text depressed @click="close">
          <span v-once>{{ $t('options.cancel') }}</span>
        </v-btn>
        <v-btn depressed color="primary" :disabled="!selectedAgents.length" @click="handleSaveChanges">
          <span v-once>{{ $t('options.save') }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment-timezone';
import { mapGetters } from 'vuex';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsDateTimePicker from '@/components/ecs-datetime-picker/EcsDateTimePicker';

export default {
  name: 'EcsTempAssignExpirationDialog',

  components: { EcsAvatar, EcsDateTimePicker },

  props: {
    businessProcessId: {
      type: String,
      required: true
    },
    agents: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      expirationTime: null,
      availableAgents: [],
      dialog: false
    };
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', ['supervisedBusinessProcesses']),

    ...mapGetters('SETTINGS', ['applicationSettings']),

    businessProcess() {
      return this.supervisedBusinessProcesses.find(
        businessProcess => businessProcess.businessProcessId === this.businessProcessId
      );
    },

    description() {
      const { businessProcessName } = this.businessProcess;
      return this.$t('dialogs.assignmentsExpirationTime.description', {
        businessProcessName
      });
    },

    activeAgents() {
      return this.availableAgents.filter(agent => agent.isActive);
    },

    inactiveAgents() {
      return this.availableAgents.filter(agent => !agent.isActive);
    },

    availableAgentsGrouped() {
      const result = [];

      if (this.activeAgents.length) {
        result.push({
          label: this.$t('labels.activeAgents'),
          agents: this.activeAgents,
          selectedAgents: this.activeAgents.filter(agent => agent.selected),
          onSelectAll: this.selectAllActiveAgents
        });
      }

      if (this.inactiveAgents.length) {
        result.push({
          label: this.$t('labels.inactiveAgents'),
          agents: this.inactiveAgents,
          selectedAgents: this.inactiveAgents.filter(agent => agent.selected),
          onSelectAll: this.selectAllInactiveAgents
        });
      }

      return result;
    },

    selectedAgents() {
      return this.availableAgents.filter(agent => agent.selected);
    }
  },

  watch: {
    agents(value) {
      this.availableAgents = value.map(agent => ({ ...agent, selected: false }));
    }
  },

  methods: {
    open() {
      this.expirationTime = moment
        .utc()
        .add(6, 'hours')
        .toISOString();
      this.availableAgents = this.agents.map(agent => ({ ...agent, selected: false }));
      this.dialog = true;
    },

    close() {
      this.dialog = false;
    },

    getExpirationTimeFormatted(value) {
      let result = '';

      result = moment(value)
        .utcOffset(this.applicationSettings.timeZoneUtcOffset)
        .format(`YYYY-MM-DD ${this.applicationSettings.timeFormat}`);

      return result;
    },

    onDateTimeChange(value) {
      this.expirationTime = value;
    },

    selectAllActiveAgents() {
      const selected = this.activeAgents.every(agent => agent.selected);
      this.availableAgents = [
        ...this.activeAgents.map(agent => ({ ...agent, selected: !selected })),
        ...this.inactiveAgents
      ];
    },

    selectAllInactiveAgents() {
      const selected = this.inactiveAgents.every(agent => agent.selected);
      this.availableAgents = [
        ...this.activeAgents,
        ...this.inactiveAgents.map(agent => ({ ...agent, selected: !selected }))
      ];
    },

    handleSaveChanges() {
      this.$emit('update-assignments', {
        businessProcessId: this.businessProcessId,
        agentIds: this.selectedAgents.map(agent => agent.id),
        expirationTime: moment.utc(this.expirationTime).format('YYYY-MM-DD HH:mm:ss')
      });
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.no-agents {
  text-align: center;
  font-size: 12px;
  padding: 20px 0;
  opacity: 0.5;
}

.agents-container {
  overflow: auto;
  width: 100%;
  height: 360px;

  .agents-group-title {
    margin: 8px 16px;
    display: inline-grid;
    grid-template-columns: auto 160px;
    grid-template-rows: 20px;
    width: calc(100% - 32px);
    font-size: 12px;
    text-transform: uppercase;
    line-height: 20px;

    .agents-group-label {
      color: var(--v-primary-base);
    }

    .agents-group-selection {
      display: inline-flex;
      justify-self: end;

      .agents-group-selection-box {
        & ::v-deep .v-icon {
          font-size: 16px;
        }
      }
    }
  }

  .agents-group-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, 220px);
    grid-template-rows: repeat(auto-fill, 40px);
    grid-gap: 8px 8px;
    justify-content: center;
    padding: 8px 0;

    .agent {
      height: 40px;
      float: left;
      display: grid;
      grid-template-columns: 40px auto 24px;
      cursor: pointer;
      transition: all 0.3s ease;

      &[dark]:hover {
        background-color: var(--v-surface-base);
      }

      &:hover {
        background-color: var(--v-surface-darken1);
      }

      .agent-avatar {
        padding: 4px;
        align-self: center;

        & > * {
          user-select: none;
          pointer-events: none;
        }
      }

      .agent-content {
        opacity: 0.75;
        font-size: 13px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;

        .name {
          padding: 2px 0 0;
        }

        .details {
          font-size: 11px;
          opacity: 0.75;
        }
      }

      .agent-selection {
        margin: 0;
        padding: 0;
        display: inline-grid;
        align-content: center;
        justify-content: center;
      }
    }
  }
}
</style>
